import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useLocation, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarDays,
  ListChecks,
  Briefcase,
  Camera,
  CalendarClock,
  Users,
  Settings as SettingsIcon,
  Sun,
  Moon,
  Command,
  FileText,
  Maximize2,
  CreditCard,
  ShieldCheck,
  LogOut,
  Plus,
  Folder,
  Sparkles,
  Package,
  Heart,
  Plane,
  BookOpen,
  Wrench,
  LayoutGrid,
} from "lucide-react";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import BulletHole from "./BulletHole"; // eslint-disable-line no-unused-vars
import NewTabModal from "./NewTabModal";

const ICON_MAP = {
  Folder, Sparkles, Briefcase, Camera, Package, Heart, Plane, BookOpen, Wrench, Users,
};

const CORE_TABS = [
  { to: "/", label: "Overview", icon: LayoutDashboard, key: "1" },
  { to: "/month", label: "Month", icon: CalendarDays, key: "2" },
  { to: "/weekly", label: "Weekly", icon: ListChecks, key: "3" },
  { to: "/board", label: "Board", icon: LayoutGrid, key: "4" },
  { to: "/deals", label: "Pipeline", icon: Briefcase, key: "5" },
];
const POST_TABS = [
  { to: "/team", label: "Team", icon: Users },
  { to: "/calendar", label: "Calendar", icon: CalendarClock },
  { to: "/briefing", label: "Briefing", icon: FileText },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

const pad = (n) => String(n).padStart(2, "0");

const trialRemaining = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "EXPIRED";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${pad(m)}m left`;
};

export default function Layout({ children, onCommandOpen, onDeckOpen }) {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [dynamicTabs, setDynamicTabs] = useState([]);
  const [newTabOpen, setNewTabOpen] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);

  const loadTabs = useCallback(async () => {
    try {
      const t = await api.get("/tabs").then((r) => r.data);
      setDynamicTabs(Array.isArray(t) ? t : []);
    } catch {
      setDynamicTabs([]);
    }
  }, []);

  useEffect(() => {
    if (user && user !== false) loadTabs();
  }, [user, loadTabs]);

  // Refresh when route changes (catches creates/deletes from elsewhere)
  useEffect(() => {
    if (user && user !== false) loadTabs();
  }, [loc.pathname, user, loadTabs]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => setMobileOpen(false), [loc.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      const target = e.target;
      const inField =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onCommandOpen?.();
        return;
      }
      if (inField) return;
      if (e.key.toLowerCase() === "f") {
        e.preventDefault();
        onDeckOpen?.();
        return;
      }
      // 1-4 = core, 5+ = dynamic, rest = post
      const k = e.key;
      const coreIdx = CORE_TABS.findIndex((t) => t.key === k);
      if (coreIdx >= 0) {
        e.preventDefault();
        navigate(CORE_TABS[coreIdx].to);
        return;
      }
      const n = parseInt(k, 10);
      if (!isNaN(n)) {
        const dynIdx = n - 5;
        if (dynIdx >= 0 && dynIdx < dynamicTabs.length) {
          e.preventDefault();
          navigate(`/tab/${dynamicTabs[dynIdx].id}`);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, onCommandOpen, onDeckOpen, dynamicTabs]);

  const dateLine = now.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const timeLine = `${pad(now.getHours())}:${pad(now.getMinutes())}`;

  const sub = user?.subscription_status;
  const isAdmin = user?.role === "admin";
  const plan = user?.plan || "free";
  const planChip = !isAdmin && (
    <Link
      to="/billing"
      className={`hud-chip whitespace-nowrap ${plan !== "free" ? "hud-chip-on" : ""}`}
      data-testid="plan-chip"
    >
      <span className="status-dot" /> {plan.toUpperCase()}
    </Link>
  );
  const adminChip = isAdmin && (
    <Link to="/admin" className="hud-chip whitespace-nowrap" style={{ borderColor: "hsl(var(--signal-amber))", color: "hsl(var(--signal-amber))" }}>
      <ShieldCheck size={10} /> Admin
    </Link>
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground relative z-[2]">
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-60 border-r border-border bg-background flex flex-col
          transition-transform duration-300 ${
            mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
        data-testid="sidebar"
      >
        <div className="px-6 pt-7 pb-5 relative">
          <div className="readout mb-2">
            <span className="status-dot" /> <strong>Live</strong>
          </div>
          <h1 className="display text-2xl font-bold leading-none tracking-tight uppercase">
            Command<span className="text-oud">·</span>Center
          </h1>
          <div className="hairline-oud w-12 mt-3" />
          <div className="serif-mark text-[11px] text-muted-foreground mt-1.5 tracking-wide">
            For CEO's Only
          </div>
        </div>

        <div className="tread-plate h-1.5" />

        <nav className="flex-1 py-3 overflow-y-auto" data-testid="primary-nav">
          {CORE_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink key={t.to} to={t.to} end={t.to === "/"} data-testid={`nav-${t.label.toLowerCase()}`}>
                {({ isActive }) => (
                  <div className="ox-tab" data-active={isActive}>
                    <Icon size={14} strokeWidth={1.8} className="tab-icon" />
                    <span className="flex-1">{t.label}</span>
                  </div>
                )}
              </NavLink>
            );
          })}

          {/* Dynamic tabs */}
          {dynamicTabs.length > 0 && (
            <div className="px-5 pt-4 pb-1 kpi-label">My Tabs</div>
          )}
          {dynamicTabs.map((t) => {
            const Icon = ICON_MAP[t.icon] || Folder;
            const isDragging = dragId === t.id;
            const isDropTarget = dropTargetId === t.id && dragId && dragId !== t.id;
            return (
              <div
                key={t.id}
                draggable
                onDragStart={(e) => {
                  setDragId(t.id);
                  e.dataTransfer.effectAllowed = "move";
                  try { e.dataTransfer.setData("text/plain", t.id); } catch {}
                }}
                onDragOver={(e) => {
                  if (!dragId || dragId === t.id) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDropTargetId(t.id);
                }}
                onDragLeave={() => {
                  if (dropTargetId === t.id) setDropTargetId(null);
                }}
                onDrop={async (e) => {
                  e.preventDefault();
                  if (!dragId || dragId === t.id) return;
                  const next = [...dynamicTabs];
                  const fromIdx = next.findIndex((x) => x.id === dragId);
                  const toIdx = next.findIndex((x) => x.id === t.id);
                  if (fromIdx < 0 || toIdx < 0) return;
                  const [moved] = next.splice(fromIdx, 1);
                  next.splice(toIdx, 0, moved);
                  setDynamicTabs(next);
                  setDragId(null);
                  setDropTargetId(null);
                  try {
                    await api.post("/tabs/reorder", { ids: next.map((x) => x.id) });
                  } catch {
                    loadTabs();
                  }
                }}
                onDragEnd={() => { setDragId(null); setDropTargetId(null); }}
                className={`relative ${isDragging ? "opacity-40" : ""}`}
                data-testid={`nav-tab-wrap-${t.id}`}
              >
                {isDropTarget && (
                  <div className="absolute left-0 right-0 -top-px h-0.5 bg-[hsl(var(--oxblood))] z-10" />
                )}
                <NavLink
                  to={`/tab/${t.id}`}
                  data-testid={`nav-tab-${t.id}`}
                >
                  {({ isActive }) => (
                    <div className="ox-tab" data-active={isActive}>
                      <Icon size={14} strokeWidth={1.8} className="tab-icon" />
                      <span className="flex-1 truncate">{t.label}</span>
                      {t.system && (
                        <span className="mono text-[8px] tracking-widest opacity-50">SYS</span>
                      )}
                    </div>
                  )}
                </NavLink>
              </div>
            );
          })}
          {plan === "free" && !isAdmin ? (
            <NavLink to="/billing" className="ox-tab w-full text-left hover:text-foreground opacity-70" data-testid="add-tab-btn">
              <Plus size={14} strokeWidth={1.8} className="tab-icon" />
              <span className="flex-1">New tab</span>
              <span className="mono text-[8px] tracking-widest text-oud">PRO</span>
            </NavLink>
          ) : (
          <button
            onClick={() => setNewTabOpen(true)}
            className="ox-tab w-full text-left hover:text-foreground"
            data-testid="add-tab-btn"
          >
            <Plus size={14} strokeWidth={1.8} className="tab-icon" />
            <span className="flex-1">New tab</span>
          </button>
          )}

          <div className="px-5 pt-4 pb-1 kpi-label">Tools</div>
          {POST_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink key={t.to} to={t.to} data-testid={`nav-${t.label.toLowerCase()}`}>
                {({ isActive }) => (
                  <div className="ox-tab" data-active={isActive}>
                    <Icon size={14} strokeWidth={1.8} className="tab-icon" />
                    <span className="flex-1">{t.label}</span>
                  </div>
                )}
              </NavLink>
            );
          })}

          <div className="px-5 pt-4 pb-1 kpi-label">Account</div>
          <NavLink to="/billing" data-testid="nav-billing">
            {({ isActive }) => (
              <div className="ox-tab" data-active={isActive}>
                <CreditCard size={14} strokeWidth={1.8} className="tab-icon" />
                <span className="flex-1">Billing</span>
              </div>
            )}
          </NavLink>
          {isAdmin && (
            <NavLink to="/admin" data-testid="nav-admin">
              {({ isActive }) => (
                <div className="ox-tab" data-active={isActive}>
                  <ShieldCheck size={14} strokeWidth={1.8} className="tab-icon" />
                  <span className="flex-1">Reseller</span>
                </div>
              )}
            </NavLink>
          )}
        </nav>

        <div className="tread-plate h-1.5" />

        <div className="p-5 space-y-2">
          <div className="hud-frame px-3 py-2.5 flex items-center justify-between" data-testid="user-pill">
            <div className="min-w-0">
              <div className="kpi-label">Account</div>
              <div className="mono text-xs truncate mt-0.5">{user?.email || "—"}</div>
            </div>
            <button onClick={logout} className="hud-btn !px-2 !py-1" title="Sign out" data-testid="logout-btn">
              <LogOut size={11} />
            </button>
          </div>
          <button onClick={onDeckOpen} className="hud-btn hud-btn-primary w-full justify-between" data-testid="open-deck">
            <span className="flex items-center gap-2">
              <Maximize2 size={11} /> Command Deck
            </span>
            <span className="opacity-70">F</span>
          </button>
          <button onClick={onCommandOpen} className="hud-btn w-full justify-between" data-testid="open-command-palette">
            <span className="flex items-center gap-2">
              <Command size={11} /> Jump
            </span>
            <span className="opacity-60">⌘K</span>
          </button>
          <button onClick={toggle} className="hud-btn w-full justify-between" data-testid="theme-toggle">
            <span className="flex items-center gap-2">
              {theme === "dark" ? <Sun size={11} /> : <Moon size={11} />}
              {theme === "dark" ? "Light" : "Dark"}
            </span>
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/70 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <main className="flex-1 min-w-0 flex flex-col relative z-[2]">
        <header
          className="sticky top-0 z-20 border-b border-border bg-background/92 backdrop-blur-sm"
          data-testid="topbar"
        >
          <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-4 lg:px-8 h-14">
            <button onClick={() => setMobileOpen((v) => !v)} className="hud-btn lg:hidden !px-2.5 !py-2" data-testid="mobile-menu-toggle" aria-label="Open menu">
              ☰
            </button>
            <div className="mono uppercase text-[10px] sm:text-xs tracking-[0.22em] text-muted-foreground truncate flex-shrink min-w-0" data-testid="today-label">
              <span className="hidden xs:inline">{dateLine}</span>
              <span className="xs:hidden">{dateLine.split(",")[0]}</span>
              <span className="text-foreground ml-2 sm:ml-3">{timeLine}</span>
            </div>
            <span className="flex-1" />
            {adminChip}
            {planChip}
          </div>
        </header>

        <div className="flex-1 px-3 sm:px-4 lg:px-8 py-5 sm:py-6 lg:py-10 pb-24 animate-fadein relative z-[2]">
          {children}
        </div>
      </main>

      <NewTabModal
        open={newTabOpen}
        onClose={() => setNewTabOpen(false)}
        onCreated={async (t) => {
          await loadTabs();
          navigate(`/tab/${t.id}`);
        }}
      />
    </div>
  );
}
